import { createSelector } from 'reselect';

function extract(items, id, comparisonFn) {
  if (items && Array.isArray(items[id])) {
    return items[id].find(comparisonFn) || null;
  }
  return undefined;
}

export const buildAccount = (accountsData, accountId, user, type = 'client') => {
  const comparisonFn = type === 'advisor' ? (v, i) => i === 0 : (v) => v.userId === user.id;

  const {
    items,
    details,
    fees,
    riskAppetiteProfiles,
    financialAssets,
    lifestyleCosts,
    financialExperiences,
    charts,
    owners,
    investmentAgreements,
    amlProfiles,
    taxSchemes,
    contracts,
    documents,
    aums,
  } = accountsData;
  if (!user) return null;
  if (!items) return null;
  const account = items.find((a) => a.id === accountId);
  if (!account) return null;

  return {
    ...account,
    ...(details[accountId] ?? {}),
    fees: fees[accountId],
    riskAppetiteProfiles: riskAppetiteProfiles[accountId],
    riskAppetiteProfile: extract(riskAppetiteProfiles, accountId, (v, i) => i === 0),
    financialAssets: extract(financialAssets, accountId, comparisonFn),
    lifestyleCosts: extract(lifestyleCosts, accountId, comparisonFn),
    financialExperiences: extract(financialExperiences, accountId, comparisonFn),
    owner: extract(owners, accountId, comparisonFn),
    amlProfile: extract(amlProfiles, accountId, comparisonFn),
    taxScheme: extract(taxSchemes, accountId, comparisonFn),
    // we may need to add a check for the userId signers
    contracts: contracts[accountId],
    documents: documents[accountId],
    owners: owners[accountId],
    charts: charts[accountId],
    aums: aums[accountId],
    investmentAgreements,
  };
};

const selectUser = (state) => state.app.user;
export const selectAccounts = (state) => state.accounts;
export const selectCurrentAccountId = (state) => state.accounts.accountId;

export const selectCurrentAccount = createSelector(
  selectAccounts,
  selectCurrentAccountId,
  selectUser,
  buildAccount
);

export const selectAccount = createSelector(
  selectAccounts,
  (_, id) => id,
  selectUser,
  buildAccount
);

export const selectCurrentAccountAsAdvisor = createSelector(
  selectAccounts,
  selectCurrentAccountId,
  selectUser,
  () => 'advisor',
  buildAccount
);

export const selectAccountAsAdvisor = createSelector(
  selectAccounts,
  (_, id) => id,
  selectUser,
  () => 'advisor',
  buildAccount
);

export const selectCurrentAccountSimulatedRiskProfile = createSelector(
  selectAccounts,
  selectCurrentAccountId,
  (accounts, id) => accounts.simulatedRiskProfiles[id]
);

export const selectCurrentAmlProfile = createSelector(
  selectUser,
  selectAccounts,
  selectCurrentAccountId,
  (user, accounts, accountId) => {
    const amlProfiles = accounts.amlProfiles[accountId];
    return amlProfiles?.find((p) => p.userId === user.id);
  }
);
